import axios from 'axios';
import KeycloakService from 'services/KeycloakService';

const PYAPI_URL = process.env.REACT_APP_PYAPI_URL as string;

export interface AiWorkflowTrialStatus {
  applicable: boolean;
  used: boolean;
}

export interface TrialUsedError extends Error {
  trialUsed: true;
}

const authHeader = () => ({
  Authorization: `Bearer ${KeycloakService.getToken()}`,
});

const eventOf = (line: string): string | null => {
  try {
    return JSON.parse(line)?.event ?? null;
  } catch {
    return null;
  }
};

export class BeneficialOwnershipAPI {
  static async getTrialStatus(groupId: string): Promise<AiWorkflowTrialStatus> {
    const response = await axios.get(`${PYAPI_URL}/ai-workflows/trial-status`, {
      params: { group_id: groupId, bucket: 'beneficial_ownership' },
      headers: authHeader(),
    });
    return response.data;
  }

  static async startDetachedRun(body: Record<string, unknown>): Promise<void> {
    const response = await fetch(`${PYAPI_URL}/beneficial-ownership/workflow`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/x-ndjson',
        ...authHeader(),
      },
      body: JSON.stringify(body),
    });
    if (response.status === 402) {
      const detail: string = await response
        .json()
        .then((data) => data?.detail || '')
        .catch(() => '');
      const error = new Error(
        detail || 'Your team’s trial of Beneficial Ownership has been used.',
      ) as TrialUsedError;
      error.trialUsed = true;
      throw error;
    }
    if (!response.ok || !response.body) {
      throw new Error('The investigation could not be started. Try again.');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let outcome: 'scheduled' | 'error' | 'unexpected' | null = null;
    const read = async (): Promise<void> => {
      const { done, value } = await reader.read();
      if (done) return;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      lines.forEach((line) => {
        if (outcome || !line.trim()) return;
        const event = eventOf(line);
        if (event === 'notify_scheduled') outcome = 'scheduled';
        else if (event === 'workflow_error') outcome = 'error';
        else if (event === 'followup' || event === 'phase_paused') {
          outcome = 'unexpected';
        }
      });
      if (outcome) {
        reader.cancel().catch(() => undefined);
        return;
      }
      await read();
    };
    await read();
    if (outcome !== 'scheduled') {
      throw new Error('The investigation could not be started. Try again.');
    }
  }
}
